<template>
  <div class="notulensi-page">
    <div class="main-container">
      <div class="page-header">
        <h2 class="page-title">Notulensi</h2>
      </div>

      <div class="actions-bar">
        <a-input
          v-model:value="filters.search"
          placeholder="Cari nama kegiatan"
          class="search-input"
          @input="handleSearchInput"
          @pressEnter="fetchNotulensi"
        >
          <template #prefix>
            <SearchOutlined style="color: rgba(0, 0, 0, 0.25)" />
          </template>
        </a-input>

        <a-button
          v-if="isSekretaris"
          type="primary"
          size="large"
          class="btn-tambah"
          @click="handleTambah"
        >
          <template #icon>
            <PlusOutlined />
          </template>
          TAMBAH
        </a-button>
      </div>

      <a-table
        row-key="id"
        class="notulensi-table"
        :columns="columns"
        :data-source="items"
        :loading="loading"
        :pagination="tablePagination"
        :custom-row="customRow"
        :scroll="{ x: isSekretaris ? 980 : 820 }"
        @change="handleTableChange"
      >
        <template #filterDropdown="{ column }">
          <div v-if="column.key === 'waktuKegiatan'" class="date-filter">
            <a-range-picker
              v-model:value="dateRange"
              format="DD-MM-YYYY"
              :placeholder="['Tanggal Mulai', 'Tanggal Akhir']"
              class="date-filter-picker"
              @change="handleDateRangeChange"
            />
          </div>
        </template>

        <template #namaKegiatan="{ text }">
          <span class="activity-name">{{ text || '-' }}</span>
        </template>

        <template #waktuKegiatan="{ text }">
          {{ formatDate(text) }}
        </template>

        <template #jenisKegiatan="{ text }">
          <a-tag :color="text === 'Internal' ? 'red' : 'blue'" class="type-tag">
            {{ text || '-' }}
          </a-tag>
        </template>

        <template #aksi="{ record }">
          <div class="action-buttons">
            <a-tooltip title="Edit notulensi">
              <a-button shape="circle" class="action-icon" @click.stop="handleEdit(record)">
                <template #icon>
                  <EditOutlined />
                </template>
              </a-button>
            </a-tooltip>
            <a-tooltip title="Hapus notulensi">
              <a-button
                shape="circle"
                class="action-icon danger"
                @click.stop="openDeleteModal(record)"
              >
                <template #icon>
                  <DeleteOutlined />
                </template>
              </a-button>
            </a-tooltip>
          </div>
        </template>
      </a-table>
    </div>

    <a-modal
      v-model:visible="viewModal"
      centered
      :footer="null"
      width="720px"
      class="notulensi-view-modal"
      @cancel="selectedRecord = null"
    >
      <template #title>
        <div class="modal-title-block">
          <span>Detail Notulensi</span>
          <small>{{ selectedRecord ? formatDate(selectedRecord.waktuKegiatan) : '-' }}</small>
        </div>
      </template>

      <a-spin :spinning="detailLoading">
        <div v-if="selectedRecord" class="detail-content">
          <div class="detail-section">
            <div class="detail-label">Nama Kegiatan</div>
            <div class="detail-value strong">{{ selectedRecord.namaKegiatan || '-' }}</div>
          </div>

          <div class="detail-grid">
            <div class="detail-section">
              <div class="detail-label">Waktu Kegiatan</div>
              <div class="detail-value">{{ formatDate(selectedRecord.waktuKegiatan) }}</div>
            </div>
            <div class="detail-section">
              <div class="detail-label">Jenis Kegiatan</div>
              <div class="detail-value">
                <a-tag :color="selectedRecord.jenisKegiatan === 'Internal' ? 'red' : 'blue'">
                  {{ selectedRecord.jenisKegiatan || '-' }}
                </a-tag>
              </div>
            </div>
          </div>

          <a-divider />

          <div class="detail-section">
            <div class="detail-label">File Notulensi & Lainnya</div>
            <div v-if="selectedRecord.files && selectedRecord.files.length" class="attachment-list">
              <div
                v-for="file in selectedRecord.files"
                :key="file.id"
                class="d-flex align-items-center mb-2 file-link"
              >
                <PaperClipOutlined class="mr-2 file-paperclip-icon" />
                <div class="flex-grow-1">
                  <span class="mr-4 custom-text-anchor-not-clickable">
                    {{ file.fileName }}
                  </span>
                  <EyeOutlined class="custom-icon-unggah mr-2" @click="() => openFile(file)" />
                  <ExportOutlined class="custom-icon-unggah mr-2" @click="() => openFile(file)" />
                </div>
              </div>
            </div>
            <div v-else class="empty-state">Tidak ada file.</div>
          </div>

          <div class="detail-section">
            <div class="detail-label">Link</div>
            <div v-if="selectedRecord.links && selectedRecord.links.length" class="link-list">
              <div
                v-for="link in selectedRecord.links"
                :key="link.id"
                class="d-flex align-items-center mb-2 file-link"
              >
                <LinkOutlined class="mr-2 file-paperclip-icon" />
                <div class="flex-grow-1">
                  <span class="mr-4 custom-text-anchor" @click="() => openExternal(link.url)">
                    {{ link.title || link.url }}
                  </span>
                  <small v-if="link.title && link.url" class="link-url">{{ link.url }}</small>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">Tidak ada link.</div>
          </div>
        </div>
      </a-spin>
    </a-modal>

    <a-modal v-model:visible="deleteModal" centered :footer="null" title="Hapus Notulensi?">
      <p class="delete-copy">
        Data notulensi yang dihapus tidak dapat dikembalikan. Pastikan data yang dipilih sudah
        benar.
      </p>
      <div class="modal-actions">
        <button type="button" class="modal-button secondary" @click="deleteModal = false">
          KEMBALI
        </button>
        <button
          type="button"
          class="modal-button danger"
          :disabled="deleting"
          @click="handleDelete"
        >
          {{ deleting ? 'MENGHAPUS...' : 'YA, HAPUS' }}
        </button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import moment from 'moment'
import 'moment/locale/id'
import { notification } from 'ant-design-vue'
import {
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  EyeOutlined,
  LinkOutlined,
  PaperClipOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons-vue'
import { getFileUrl, notulensiAPI } from '@/services/axios/perki'

moment.locale('id')

export default defineComponent({
  name: 'NotulensiList',
  components: {
    DeleteOutlined,
    EditOutlined,
    ExportOutlined,
    EyeOutlined,
    LinkOutlined,
    PaperClipOutlined,
    PlusOutlined,
    SearchOutlined,
  },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const detailLoading = ref(false)
    const deleting = ref(false)
    const viewModal = ref(false)
    const deleteModal = ref(false)
    const selectedRecord = ref(null)
    const items = ref([])
    const dateRange = ref(null)
    let searchTimer = null

    const filters = reactive({
      search: '',
      jenisKegiatan: null,
      dateFrom: null,
      dateTo: null,
      sortBy: 'waktuKegiatan',
      sortOrder: 'DESC',
    })

    const pagination = reactive({
      page: 1,
      limit: 10,
      total: 0,
    })

    const currentRole = computed(() => {
      try {
        const user = JSON.parse(localStorage.getItem('perki_user') || '{}')
        return localStorage.getItem('perki_role') || user.role || ''
      } catch (error) {
        return localStorage.getItem('perki_role') || ''
      }
    })

    const isSekretaris = computed(() => currentRole.value === 'sekretaris')

    const columns = computed(() => {
      const baseColumns = [
        {
          title: 'Nama Kegiatan',
          dataIndex: 'namaKegiatan',
          key: 'namaKegiatan',
          sorter: true,
          width: 360,
          slots: { customRender: 'namaKegiatan' },
        },
        {
          title: 'Waktu Kegiatan',
          dataIndex: 'waktuKegiatan',
          key: 'waktuKegiatan',
          sorter: true,
          width: 190,
          slots: {
            customRender: 'waktuKegiatan',
            filterDropdown: 'filterDropdown',
          },
          filteredValue: dateRange.value ? [dateRange.value] : null,
        },
        {
          title: 'Jenis Kegiatan',
          dataIndex: 'jenisKegiatan',
          key: 'jenisKegiatan',
          width: 170,
          filters: [
            { text: 'Internal', value: 'Internal' },
            { text: 'Eksternal', value: 'Eksternal' },
          ],
          filteredValue: filters.jenisKegiatan ? [filters.jenisKegiatan] : null,
          slots: { customRender: 'jenisKegiatan' },
        },
      ]

      if (isSekretaris.value) {
        baseColumns.push({
          title: 'Aksi',
          key: 'aksi',
          width: 120,
          align: 'center',
          slots: { customRender: 'aksi' },
        })
      }

      return baseColumns
    })

    const tablePagination = computed(() => ({
      current: pagination.page,
      pageSize: pagination.limit,
      total: pagination.total,
      showSizeChanger: true,
      showTotal: total => `Total ${total} data`,
    }))

    const fetchNotulensi = async () => {
      loading.value = true
      try {
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
        }

        if (filters.search) params.search = filters.search
        if (filters.jenisKegiatan) params.jenisKegiatan = filters.jenisKegiatan
        if (filters.dateFrom) params.dateFrom = filters.dateFrom
        if (filters.dateTo) params.dateTo = filters.dateTo

        const response = await notulensiAPI.getAll(params)
        items.value = response.data?.data || []
        pagination.total = response.data?.pagination?.total || 0
      } catch (error) {
        notification.error({
          message: 'Gagal Memuat Notulensi',
          description: error.response?.data?.message || 'Silakan coba lagi beberapa saat lagi.',
        })
      } finally {
        loading.value = false
      }
    }

    const handleSearchInput = () => {
      if (searchTimer) {
        clearTimeout(searchTimer)
      }
      searchTimer = setTimeout(() => {
        pagination.page = 1
        fetchNotulensi()
      }, 450)
    }

    const handleDateRangeChange = value => {
      if (value && value.length === 2) {
        filters.dateFrom = value[0].format('YYYY-MM-DD')
        filters.dateTo = value[1].format('YYYY-MM-DD')
      } else {
        filters.dateFrom = null
        filters.dateTo = null
      }
      pagination.page = 1
      fetchNotulensi()
    }

    const handleTableChange = (pager, tableFilters, sorter) => {
      pagination.page = pager.current || 1
      pagination.limit = pager.pageSize || 10
      filters.jenisKegiatan =
        tableFilters && tableFilters.jenisKegiatan && tableFilters.jenisKegiatan.length
          ? tableFilters.jenisKegiatan[0]
          : null

      if (sorter && sorter.order) {
        filters.sortBy = sorter.field || sorter.columnKey || 'waktuKegiatan'
        filters.sortOrder = sorter.order === 'ascend' ? 'ASC' : 'DESC'
      } else {
        filters.sortBy = 'waktuKegiatan'
        filters.sortOrder = 'DESC'
      }

      fetchNotulensi()
    }

    const formatDate = date => {
      if (!date) return '-'
      return moment(date).format('DD MMMM YYYY, HH:mm')
    }

    const openView = async record => {
      selectedRecord.value = record
      viewModal.value = true
      detailLoading.value = true
      try {
        const response = await notulensiAPI.getById(record.id)
        selectedRecord.value = response.data?.data || record
      } catch (error) {
        notification.error({
          message: 'Gagal Memuat Detail',
          description: error.response?.data?.message || 'Detail notulensi tidak dapat dimuat.',
        })
      } finally {
        detailLoading.value = false
      }
    }

    const customRow = record => ({
      onClick: () => openView(record),
      class: 'notulensi-clickable-row',
    })

    const handleTambah = () => {
      router.push('/notulensi/tambah')
    }

    const handleEdit = record => {
      router.push(`/notulensi/edit/${record.id}`)
    }

    const openDeleteModal = record => {
      selectedRecord.value = record
      deleteModal.value = true
    }

    const handleDelete = async () => {
      if (!selectedRecord.value) return

      deleting.value = true
      try {
        await notulensiAPI.delete(selectedRecord.value.id)
        notification.success({
          message: 'Notulensi Dihapus',
          description: 'Data notulensi berhasil dihapus.',
        })
        deleteModal.value = false
        selectedRecord.value = null
        fetchNotulensi()
      } catch (error) {
        notification.error({
          message: 'Gagal Menghapus',
          description: error.response?.data?.message || 'Data notulensi gagal dihapus.',
        })
      } finally {
        deleting.value = false
      }
    }

    const openFile = file => {
      const url = getFileUrl(file.fileUrl)
      if (url) {
        window.open(url, '_blank')
      }
    }

    const openExternal = url => {
      if (!url) return
      const targetUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`
      window.open(targetUrl, '_blank')
    }

    onMounted(fetchNotulensi)

    onBeforeUnmount(() => {
      if (searchTimer) {
        clearTimeout(searchTimer)
      }
    })

    return {
      columns,
      dateRange,
      deleting,
      deleteModal,
      detailLoading,
      filters,
      formatDate,
      handleDateRangeChange,
      handleDelete,
      handleEdit,
      handleSearchInput,
      handleTableChange,
      handleTambah,
      isSekretaris,
      items,
      loading,
      openDeleteModal,
      openExternal,
      openFile,
      selectedRecord,
      tablePagination,
      viewModal,
      customRow,
      fetchNotulensi,
    }
  },
})
</script>

<style lang="scss" scoped>
.notulensi-page {
  background: #fcfcfc;
  min-height: 100vh;

  .main-container {
    padding: 24px;
    background: #fcfcfc;
    min-height: calc(100vh - 64px);

    @media (max-width: 768px) {
      padding: 16px;
    }
  }
}

.page-header {
  margin-bottom: 24px;

  .page-title {
    color: #262626;
    font-size: 24px;
    font-weight: 600;
    margin: 0;
  }
}

.actions-bar {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 24px;

  .search-input {
    max-width: 520px;
    width: 100%;
  }

  .btn-tambah {
    background: #e01f28;
    border-color: #e01f28;
    border-radius: 50px;
    flex-shrink: 0;
    font-weight: 600;
    height: 40px;
    padding: 0 20px;

    &:hover,
    &:focus {
      background: #ffffff !important;
      border-color: #e10f28 !important;
      color: #e10f28 !important;
    }
  }

  @media (max-width: 768px) {
    align-items: stretch;
    flex-direction: column;

    .search-input,
    .btn-tambah {
      max-width: none;
      width: 100%;
    }
  }
}

.date-filter {
  padding: 8px;

  .date-filter-picker {
    display: block;
    margin-bottom: 8px;
    width: 280px;
  }
}

.notulensi-table {
  :deep(.ant-table) {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

    .ant-table-thead > tr > th {
      background: #f2f4f8;
      border-bottom: 1px solid #e8e8e8;
      color: #262626;
      font-weight: 600;
    }

    .ant-table-tbody > tr {
      cursor: pointer;

      &:hover > td {
        background: #fafafa;
      }
    }
  }
}

.activity-name {
  color: #262626;
  font-weight: 600;
}

.type-tag {
  border-radius: 12px;
  font-weight: 600;
  min-width: 78px;
  text-align: center;
}

.action-buttons {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: center;
}

.action-icon {
  border-color: #d9d9d9;
  color: #595959;

  &:hover,
  &:focus {
    border-color: #e10f28;
    color: #e10f28;
  }

  &.danger:hover,
  &.danger:focus {
    border-color: #e10f28;
    color: #e10f28;
  }
}

.modal-title-block {
  display: flex;
  flex-direction: column;
  gap: 4px;

  small {
    color: #8c8c8c;
    font-size: 13px;
    font-weight: 400;
  }
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.detail-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
}

.detail-section {
  .detail-label {
    color: #8c8c8c;
    font-size: 13px;
    margin-bottom: 6px;
  }

  .detail-value {
    color: #262626;
    font-size: 15px;

    &.strong {
      font-size: 18px;
      font-weight: 700;
    }
  }
}

.attachment-list,
.link-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.custom-icon-unggah {
  color: #e10f28 !important;
  cursor: pointer;
}

.custom-text-anchor-not-clickable {
  color: #321e8d;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: #e10f28;
  }
}

.custom-text-anchor {
  color: #321e8d;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: #e10f28;
    text-decoration: underline;
  }
}

.file-paperclip-icon {
  color: rgba(0, 0, 0, 0.45);
}

.file-link {
  align-items: center;
  display: inline-flex;
  gap: 4px;
  text-decoration: none;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    white-space: normal;
    word-break: break-word;
  }

  &:hover {
    text-decoration: underline;
  }
}

.link-url {
  color: #8c8c8c;
  display: block;
  font-size: 12px;
  margin-top: 2px;
  overflow-wrap: anywhere;
}

.empty-state {
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  color: #8c8c8c;
  padding: 12px;
}

.delete-copy {
  color: #595959;
  margin-bottom: 24px;
}

.modal-actions {
  display: flex;
  gap: 12px;

  @media (max-width: 576px) {
    flex-direction: column;
  }
}

.modal-button {
  border-radius: 24px;
  cursor: pointer;
  flex: 1;
  font-weight: 700;
  min-height: 40px;
  padding: 8px 14px;
  transition: all 0.2s ease;

  &.secondary {
    background: #ffffff;
    border: 2px solid #262626;
    color: #262626;

    &:hover {
      background: #262626;
      color: #ffffff;
    }
  }

  &.danger {
    background: #e10f28;
    border: 2px solid #e10f28;
    color: #ffffff;

    &:hover:not(:disabled) {
      background: #ffffff;
      color: #e10f28;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}
</style>
